"use client";

import { isRedirectError } from "next/dist/client/components/redirect-error";
import { usePathname } from "next/navigation";
import { createContext, useContext } from "react";
import { useProfile, UserProfile } from "src/api/users";
import { isPublicUrl } from "src/config/publicUrls";
import { useIsHydrated } from "src/hooks/useIsHydrated";

type AuthenticatedProfileQuery = ReturnType<typeof useProfile>;

const AuthenticatedProfileContext = createContext<AuthenticatedProfileQuery | null>(null);

/**
 * The profile the server rendered with, kept separate from the live query so
 * consumers can reproduce the server markup during hydration.
 */
const ServerRenderedProfileContext = createContext<UserProfile | null>(null);

export function shouldRetryAuthenticatedProfileQuery(
  failureCount: number,
  error: unknown,
): boolean {
  if (isRedirectError(error)) {
    return false;
  }

  return failureCount < 3;
}

export function AuthenticatedProfileProvider({
  children,
  initialProfile = null,
}: Readonly<{
  children: React.ReactNode;
  /**
   * The profile the server rendered with, read via `fetchServerProfile()`. It
   * arrives as a prop in the RSC payload rather than only as a dehydrated query,
   * because `dehydrate()` silently drops queries that did not succeed — which is
   * how a failed SSR read used to produce a signed-out server shell and a
   * signed-in client render.
   *
   * Note this prop drives what consumers *render* (see
   * {@link useHydrationSafeProfile}). Seeding the query *cache* is the root
   * layout's job, via `HydrationBoundary` — deliberately not `initialData`,
   * which React Query ignores when the key already exists.
   */
  initialProfile?: UserProfile | null;
}>) {
  // Whether to read the profile is decided from the LIVE pathname, never from a
  // prop the server computed.
  //
  // The root layout renders once per document load and Next.js reuses it across
  // every client-side navigation, so a prop would stay frozen at whichever
  // route first mounted the shell. A document that starts on /login or /logout
  // would then never start the profile query, however far the user navigates
  // into the app — consumers would silently see no profile, and a guard like
  // DocumentsRouteGuard reads that missing organization as "not the owner" and
  // redirects a legitimate user away. `usePathname()` re-renders on navigation,
  // so the query switches on exactly when the route stops being public.
  const pathname = usePathname();
  const profileQuery = useProfile({
    enabled: !isPublicUrl(pathname ?? ""),
    retry: shouldRetryAuthenticatedProfileQuery,
  });

  return (
    <ServerRenderedProfileContext.Provider value={initialProfile}>
      <AuthenticatedProfileContext.Provider value={profileQuery}>
        {children}
      </AuthenticatedProfileContext.Provider>
    </ServerRenderedProfileContext.Provider>
  );
}

export function useAuthenticatedProfile() {
  const context = useContext(AuthenticatedProfileContext);

  if (!context) {
    throw new Error("useAuthenticatedProfile must be used within AuthenticatedProfileProvider.");
  }

  return context;
}

/**
 * The profile, held stable across hydration: during SSR and the client's first
 * render this is exactly what the server rendered with, and it only follows the
 * live query once hydration has committed.
 *
 * Use this wherever the profile decides the *element tree* — a conditionally
 * rendered element, a list length, an early `return null`. Changing any of those
 * mid-hydration is what produced React #418 across the app shell.
 *
 * Use {@link useAuthenticatedProfile} instead when you need loading or error
 * state, so `data` cannot drift out of step with `isLoading`.
 */
export function useHydrationSafeProfile(): UserProfile | undefined {
  const { data } = useAuthenticatedProfile();
  const serverProfile = useContext(ServerRenderedProfileContext);
  const isHydrated = useIsHydrated();

  return isHydrated ? data : (serverProfile ?? undefined);
}

export function useOptionalAuthenticatedProfile() {
  return useContext(AuthenticatedProfileContext);
}
